import React, { useState, useEffect } from 'react';
import DocumentUploader from './components/DocumentUploader';
import SearchInterface from './components/SearchInterface';
import LandingPage from './components/LandingPage';
import LoginModal from './components/LoginModal';
import { FileText, Search, Brain, LogOut } from 'lucide-react';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [documentProcessed, setDocumentProcessed] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [documentId, setDocumentId] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('codings_token');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('codings_token');
    localStorage.removeItem('codings_email');
    setIsAuthenticated(false);
    setDocumentProcessed(false);
    setDocumentId(null);
  };

  const handleDocumentProcessed = (docId) => {
    setDocumentId(docId);
    setDocumentProcessed(true);
    setCurrentStep(2);
  };

  const resetApp = () => {
    setDocumentProcessed(false);
    setCurrentStep(1);
    setDocumentId(null);
  };

  // Show landing page if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <LandingPage onLoginClick={() => setShowLoginModal(true)} />
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <img src="/codings-logo.png" alt="CODINGS Logo" className="h-10 w-10 object-contain" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">CODINGS</h1>
                <p className="text-xs text-gray-500">Contextual Ontology-Driven Document Intelligence and Narrative Generation System</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                    <FileText className="h-4 w-4" />
                  </div>
                  <span className={`text-sm font-medium ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-500'
                    }`}>
                    Upload
                  </span>
                </div>
                <div className="w-8 h-0.5 bg-gray-300"></div>
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                    <Search className="h-4 w-4" />
                  </div>
                  <span className={`text-sm font-medium ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-500'
                    }`}>
                    Search
                  </span>
                </div>
              </div>
              <div className="h-8 w-px bg-gray-300"></div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">
                  {localStorage.getItem('codings_email')}
                </span>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-fade-in">
          {!documentProcessed ? (
            <div className="text-center">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Upload Your Document
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Upload a PDF or text document to start. We'll process it into chunks,
                  generate embeddings, and enable intelligent search through your content.
                </p>
              </div>
              <DocumentUploader onDocumentProcessed={handleDocumentProcessed} />
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    RAG Search Interface
                  </h2>
                  <p className="text-lg text-gray-600">
                    Search through your document using natural language queries.
                    We'll find the most relevant chunks for you.
                  </p>
                </div>
                <button
                  onClick={resetApp}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Upload New Document
                </button>
              </div>
              <SearchInterface documentId={documentId} />
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600">
            <p className="mb-2">© 2024 CODINGS. Powered by AI embeddings and semantic search.</p>
            <p className="text-sm">
              Credits: {' '}
              <a
                href="https://www.linkedin.com/in/sankar4"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Sankar Balasubramanian
              </a>
              {' '}•{' '}
              <a
                href="https://sankar.studio"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                sankar.studio
              </a>
              {' '}and{' '}
              <a
                href="https://www.linkedin.com/in/aayyushi-jain/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Aayushi Jain
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App; 